/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useState} from "react";
import {Button, Col, Container, FormControl, OverlayTrigger, Popover, Row} from "react-bootstrap";
import {KeyboardUtils} from "../../../Utils";
import {SB_ENTER_KEY_CODE} from "../../../Constants";
import {SbMap} from "../../../Components/Common/SbMap/SbMap";
import {Glyphicon} from "../../../Components/Common/GlyphIcon";
import {GenericModelUtils} from "../../../Utils/GenericModelUtils";
import {service as ArcGisRestService} from "../../../Services/ArcGisRestService";

export function LatLongOnMapFieldEditor(props) {

	const {FieldDefs, FieldDef, Value, OnUpdate, OnUpdates, Data} = props;
	const {keyPrefix = '', key: fieldDefKey, group: fieldDefGroup} = FieldDef;
	const latitudeFieldKey = `${keyPrefix}latitude`;
	const longitudeFieldKey = `${keyPrefix}longitude`;

	const latitudeField = GenericModelUtils.findFieldDefinition(FieldDefs, fieldDefGroup, latitudeFieldKey);
	const longitudeField = GenericModelUtils.findFieldDefinition(FieldDefs, fieldDefGroup, longitudeFieldKey);
	
	const latitudeValue = latitudeField?.getter(Data) ?? null;
	const longitudeValue = longitudeField?.getter(Data) ?? null;
	
	const defaultLatitude = 45.5;
	const defaultLongitude = -122.6;

	const defaultMarkerLocation = {
		lat: latitudeValue ?? defaultLatitude,
		lng: longitudeValue ?? defaultLongitude,
	};

	const [show, setShow] = useState(false);
	const [search, setSearch] = useState("");
	const [mapType, setMapType] = useState("Topography");
	const [markerLocation, setMarkerLocation] = useState(defaultMarkerLocation);
	const [mapLocation, setMapLocation] = useState(markerLocation);
	const [zoom, setZoom] = useState(8);

	const lookupAddress = async () => {
		const x = await ArcGisRestService.GetAddressData(search)
		const location = x?.result?.candidates?.[0]?.location;
		if (location) {
			const { y: lat, x: lng } = location;
			setMapLocation({lat, lng});
			setMarkerLocation({lat, lng});
		} else {
			alert("Address Not Found.");
		}
	}

	const updateValues = () => {
		setShow(false);

		OnUpdates?.([
			{
				def: latitudeField,
				value: Number(markerLocation.lat).toFixed(6)
			},
			{
				def: longitudeField,
				value: Number(markerLocation.lng).toFixed(6)
			}
		]);
	}
	
	const renderPopover = () => {

		let lookupAddressKeyPressHandler = (event) => {
			// when the enter key is pressed onChange hasn't happened yet
			// so we need to manually set the search state and then call lookupAddress
			setSearch(event.target.value);
			return lookupAddress();
		};

		return <Popover
			id={"sb-lat-lng-map-editor-popup-" + fieldDefKey}
			title="Click to choose a location"
			style={{}}
		>
			<Container>
				<Row className="top10">
					<Col xs={9}>
						<FormControl
							type="text"
							placeholder="Lookup an Address"
							value={search}
							onChange={(x) => setSearch(x.target.value)}
							onKeyDown={(x) => KeyboardUtils.handleKeyPress(SB_ENTER_KEY_CODE, x, lookupAddressKeyPressHandler)}
						/>
					</Col>
					<Col xs={3}>
						<Button onClick={lookupAddress}>Search</Button>
					</Col>
				</Row>
				<Row className="top10">
					<Col xs={12}>
						<SbMap
							Lat={mapLocation.lat}
							Lng={mapLocation.lng}
							Zoom={zoom}
							MapType={mapType}
							
							onClick={(e) => setMarkerLocation(e.latlng)}
							onZoomChanged={(e) => setZoom(e.target.getZoom())}
							onLocationChanged={(e) => setMapLocation(e.target.getCenter())}
							onMapTypeChanged={(name) => setMapType(name)}
							
							ClickMarker={markerLocation}
						/>
					</Col>
				</Row>
				<Row className="bottom10">
					<Col xs={12}>
						<div className="float-right top10">
							<Button variant="primary" style={{marginRight: "10px"}} onClick={updateValues}>
								<Glyphicon glyph="ok" style={{paddingRight: 0}}/>
							</Button>
							<Button onClick={() => setShow(!show)}>
								<Glyphicon glyph="remove" style={{paddingRight: 0}}/>
							</Button>
						</div>
					</Col>
				</Row>
			</Container>
		</Popover>;
	}

	return <Row className="no-gutters">
		<Col xs={10}>
			<FormControl
				type="number"
				placeholder="Latitude"
				value={latitudeValue ?? ""}
				onChange={(x) => OnUpdate(x.target.value ?? null, latitudeField)}
			/><span className="degree">&deg;</span>
		</Col>
		<Col xs={2}>
			<OverlayTrigger trigger={[]} show={!!show} overlay={renderPopover()} placement="right">
				<Glyphicon glyph="globe" className="sb-lat-lng-map-editor-control" style={{}}
				           onClick={() => setShow(!show)}/>
			</OverlayTrigger>
		</Col>
		<Col xs={10} className="top5">
			<FormControl
				type="number"
				placeholder="Longitude"
				value={longitudeValue ?? ""}
				onChange={(x) => OnUpdate(x.target.value ?? null, longitudeField)}
			/><span className="degree">&deg;</span>
		</Col>
	</Row>;
}
